import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { UserCheck, UserPlus, X, Building2 } from "lucide-react";

/**
 * Reusable "Assign to Self" + "Reassign to Executive" action pair.
 *
 * Props:
 *   itemLabel: string — short subject label shown in modal (e.g. lead client name, task title)
 *   selfAssignEndpoint: string — POST endpoint (no body)
 *   reassignEndpoint: string — POST endpoint (body = {[assigneeKey]: id})
 *   assigneeKey: string — payload key expected by backend (default "assignee_id")
 *   candidates: array of {id, name, role_name?, branch_name?}
 *   onDone: callback after successful action
 *   canSelfAssign: bool — hides "Self" button if false
 *   size: "sm" | "md"
 *   testIdPrefix: string
 */
export default function AssignmentActions({
  itemLabel,
  selfAssignEndpoint,
  reassignEndpoint,
  assigneeKey = "assignee_id",
  candidates = [],
  onDone,
  canSelfAssign = true,
  canReassign = true,
  size = "sm",
  testIdPrefix = "assignment",
}) {
  const [showPicker, setShowPicker] = useState(false);
  const [busy, setBusy] = useState(false);

  const doSelfAssign = async (e) => {
    e?.stopPropagation?.();
    if (busy) return;
    setBusy(true);
    try {
      await api.post(selfAssignEndpoint);
      toast.success("Assigned to self");
      onDone?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const doReassign = async (candId) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(reassignEndpoint, { [assigneeKey]: candId });
      toast.success("Reassigned");
      setShowPicker(false);
      onDone?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const btnCls = size === "sm"
    ? "text-[10px] px-2 py-1 flex items-center gap-1"
    : "text-xs px-3 py-1.5 flex items-center gap-1.5";

  return (
    <>
      <div className="flex items-center gap-2">
        {canSelfAssign && (
          <button
            onClick={doSelfAssign}
            disabled={busy}
            className={`iz-btn-secondary ${btnCls} disabled:opacity-50`}
            data-testid={`${testIdPrefix}-self-assign`}
            title="Assign to self"
          >
            <UserCheck className={size === "sm" ? "w-3 h-3" : "w-3.5 h-3.5"} /> Self
          </button>
        )}
        {canReassign && (
          <button
            onClick={(e) => { e?.stopPropagation?.(); setShowPicker(true); }}
            disabled={busy}
            className={`iz-btn-primary ${btnCls} disabled:opacity-50`}
            data-testid={`${testIdPrefix}-reassign`}
            title="Reassign to Executive"
          >
            <UserPlus className={size === "sm" ? "w-3 h-3" : "w-3.5 h-3.5"} /> Reassign
          </button>
        )}
      </div>

      {showPicker && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={() => setShowPicker(false)}
          data-testid={`${testIdPrefix}-picker`}
        >
          <div className="w-full max-w-md iz-card p-6 relative" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowPicker(false)}
              className="absolute top-4 right-4 p-1.5 rounded hover:bg-[#1c1c1c] text-gray-500"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-2">Reassign</p>
            <h3 className="font-display text-xl text-white mb-1">Pick an assignee</h3>
            {itemLabel && <p className="text-xs text-gray-400 mb-4">{itemLabel}</p>}
            {candidates.length === 0 ? (
              <p className="text-sm text-gray-500 italic py-4">No candidates available.</p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {candidates.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => doReassign(c.id)}
                    disabled={busy}
                    className="w-full text-left border border-[#262626] hover:border-[#d4af37]/60 rounded-md px-4 py-2.5 flex items-center justify-between transition-colors disabled:opacity-50"
                    data-testid={`${testIdPrefix}-pick-${c.id}`}
                  >
                    <div>
                      <p className="text-sm text-white">{c.name}</p>
                      <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 flex items-center gap-2 mt-0.5">
                        {c.role_name && <span>{c.role_name}</span>}
                        {c.branch_name && (
                          <span className="flex items-center gap-1">
                            <Building2 className="w-3 h-3" /> {c.branch_name}
                          </span>
                        )}
                      </p>
                    </div>
                    <UserPlus className="w-4 h-4 text-[#d4af37]" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
